import isFunction from 'lodash/isFunction';

import { isPromiseLike } from '@pressreader/types';

export type RouteData = Record<string, unknown>;
export type RouteConstraintValidateFn = (routeKey: string, routeData: RouteData) => boolean | Promise<unknown>;

export class RouteConstraint {
    readonly validate: RouteConstraintValidateFn;
    readonly failureReason: string;

    constructor(validate: RouteConstraintValidateFn = () => false, failureReason = 'value does not meet expectations') {
        if (!isFunction(validate)) {
            throw new Error('RouteConstraint: validate argument must be a function');
        }
        this.validate = validate;
        this.failureReason = failureReason;
    }

    /**
     * Checks if the URL parameter meets the constraint.
     * @param routeKey - Parameter name.
     * @param routeData - URL parameters.
     * @returns Promise resolved if valid, rejected otherwise.
     */
    matchAsync(routeKey: string, routeData: RouteData): Promise<unknown> {
        const result = this.validate(routeKey, routeData);

        if (isPromiseLike(result)) {
            return result;
        }

        return result
            ? Promise.resolve()
            : Promise.reject(new Error(`'${routeKey}' constraint failed: ${this.failureReason}; Value: ${routeData[routeKey]}`));
    }
}

/**
 * Duck-typed check for RouteConstraint.
 * @param candidate - Object to check.
 */
function isConstraint(candidate: unknown): candidate is RouteConstraint {
    return !!candidate && typeof (candidate as RouteConstraint).matchAsync === 'function';
}

/**
 * Factory for RouteConstraint.
 * @param constraint - Implementation or instance.
 */
export function createConstraint(constraint: RouteConstraintValidateFn | RouteConstraint): RouteConstraint {
    if (!constraint) {
        throw new Error('Constraint object or function required');
    }
    return isConstraint(constraint) ? constraint : new RouteConstraint(constraint);
}
